import { useState, useEffect } from 'react';
import { Mail, Save } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface AbandonedCartConfig {
  enabled: boolean;
  first_email_delay_hours: number;
  second_email_delay_hours: number;
  third_email_delay_hours: number;
  offer_discount: boolean;
  discount_code: string;
  discount_percent: number;
}

interface ReviewRequestConfig {
  enabled: boolean;
  delay_days: number;
  email_subject: string;
}

interface BackInStockConfig {
  enabled: boolean;
  button_text: string;
  email_subject: string;
}

interface EmailAutomationState {
  abandoned_cart: AbandonedCartConfig;
  review_request_emails: ReviewRequestConfig;
  back_in_stock: BackInStockConfig;
}

const DEFAULTS: EmailAutomationState = {
  abandoned_cart: {
    enabled: false,
    first_email_delay_hours: 1,
    second_email_delay_hours: 24,
    third_email_delay_hours: 72,
    offer_discount: false,
    discount_code: '',
    discount_percent: 10,
  },
  review_request_emails: {
    enabled: false,
    delay_days: 14,
    email_subject: 'How are your seeds growing?',
  },
  back_in_stock: {
    enabled: false,
    button_text: 'Notify Me When Available',
    email_subject: 'Good news! {{product_name}} is back in stock',
  },
};

interface CartStats {
  abandoned_total: number;
  recovered: number;
  recovery_rate: number;
  revenue_recovered: number;
  emails_sent: number;
}

export default function UpsellEmailAutomation() {
  const [config, setConfig] = useState<EmailAutomationState>(DEFAULTS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [stats, setStats] = useState<CartStats | null>(null);

  useEffect(() => {
    fetchConfig();
    fetchStats();
  }, []);

  async function fetchConfig() {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<{ settings: Record<string, any> }>('/admin/settings');
      const upsell = response.settings.upsell_config;
      if (upsell) {
        setConfig({
          abandoned_cart: { ...DEFAULTS.abandoned_cart, ...upsell.abandoned_cart },
          review_request_emails: { ...DEFAULTS.review_request_emails, ...upsell.review_request_emails },
          back_in_stock: { ...DEFAULTS.back_in_stock, ...upsell.back_in_stock },
        });
      }
    } catch (err) {
      setError('Failed to load email automation settings. Please try again.');
      console.error('Error fetching email automation config:', err);
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchStats() {
    try {
      const response = await api.get<CartStats>('/admin/abandoned-carts/stats');
      setStats(response);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSuccessMessage(null);

    try {
      setIsSaving(true);
      setError(null);
      const currentSettings = await api.get<{ settings: Record<string, any> }>('/admin/settings');
      const currentUpsellConfig = currentSettings.settings.upsell_config || {};

      await api.put('/admin/settings', {
        settings: {
          upsell_config: {
            ...currentUpsellConfig,
            abandoned_cart: config.abandoned_cart,
            review_request_emails: config.review_request_emails,
            back_in_stock: config.back_in_stock,
          },
        },
      });
      setSuccessMessage('Email automation settings saved successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Failed to save settings. Please try again.');
      console.error('Error saving email automation config:', err);
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading email automation settings...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Mail className="w-8 h-8 text-gray-700" aria-hidden="true" />
          <h1 className="text-3xl font-bold text-gray-900">Email Automation</h1>
        </div>
        <p className="text-gray-600">Automated emails to recover sales and engage customers</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg" role="status" aria-live="polite">
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {stats && (
        <Card title="Abandoned Cart Stats" padding="lg" className="mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-stone-600 mb-1">Total Abandoned</p>
              <p className="text-3xl font-bold text-stone-900">{stats.abandoned_total}</p>
            </div>
            <div>
              <p className="text-sm text-stone-600 mb-1">Recovered</p>
              <p className="text-3xl font-bold text-green-700">{stats.recovered}</p>
            </div>
            <div>
              <p className="text-sm text-stone-600 mb-1">Recovery Rate</p>
              <p className="text-3xl font-bold text-green-700">{stats.recovery_rate.toFixed(1)}%</p>
            </div>
            <div>
              <p className="text-sm text-stone-600 mb-1">Revenue Recovered</p>
              <p className="text-3xl font-bold text-green-700">${stats.revenue_recovered.toFixed(2)}</p>
            </div>
          </div>
        </Card>
      )}

      <form onSubmit={handleSave} noValidate>
        <div className="space-y-6">
          <Card title="Abandoned Cart Recovery" padding="lg">
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="abandoned_cart_enabled"
                  checked={config.abandoned_cart.enabled}
                  onChange={(e) => setConfig(prev => ({
                    ...prev,
                    abandoned_cart: { ...prev.abandoned_cart, enabled: e.target.checked },
                  }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="abandoned_cart_enabled" className="text-sm font-medium text-gray-700">
                  Send abandoned cart recovery emails
                </label>
              </div>

              {config.abandoned_cart.enabled && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="Email 1 Delay (hours)"
                      type="number"
                      value={String(config.abandoned_cart.first_email_delay_hours)}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        abandoned_cart: { ...prev.abandoned_cart, first_email_delay_hours: Math.max(1, parseInt(e.target.value) || 1) },
                      }))}
                      min="1"
                      helperText="Hours after cart abandoned"
                    />
                    <Input
                      label="Email 2 Delay (hours)"
                      type="number"
                      value={String(config.abandoned_cart.second_email_delay_hours)}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        abandoned_cart: { ...prev.abandoned_cart, second_email_delay_hours: Math.max(1, parseInt(e.target.value) || 24) },
                      }))}
                      min="1"
                      helperText="Hours after cart abandoned"
                    />
                    <Input
                      label="Email 3 Delay (hours)"
                      type="number"
                      value={String(config.abandoned_cart.third_email_delay_hours)}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        abandoned_cart: { ...prev.abandoned_cart, third_email_delay_hours: Math.max(1, parseInt(e.target.value) || 72) },
                      }))}
                      min="1"
                      helperText="Hours after cart abandoned"
                    />
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex items-center gap-3 mb-4">
                      <input
                        type="checkbox"
                        id="offer_discount"
                        checked={config.abandoned_cart.offer_discount}
                        onChange={(e) => setConfig(prev => ({
                          ...prev,
                          abandoned_cart: { ...prev.abandoned_cart, offer_discount: e.target.checked },
                        }))}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <label htmlFor="offer_discount" className="text-sm font-medium text-gray-700">
                        Offer discount in email 3
                      </label>
                    </div>

                    {config.abandoned_cart.offer_discount && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Discount Code"
                          value={config.abandoned_cart.discount_code}
                          onChange={(e) => setConfig(prev => ({
                            ...prev,
                            abandoned_cart: { ...prev.abandoned_cart, discount_code: e.target.value },
                          }))}
                          helperText="Coupon code to include in email"
                        />
                        <Input
                          label="Discount Percent"
                          type="number"
                          value={String(config.abandoned_cart.discount_percent)}
                          onChange={(e) => setConfig(prev => ({
                            ...prev,
                            abandoned_cart: { ...prev.abandoned_cart, discount_percent: Math.min(100, Math.max(1, parseInt(e.target.value) || 10)) },
                          }))}
                          min="1"
                          max="100"
                          helperText="Percentage off (1-100)"
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </Card>

          <Card title="Review Requests" padding="lg">
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="review_requests_enabled"
                  checked={config.review_request_emails.enabled}
                  onChange={(e) => setConfig(prev => ({
                    ...prev,
                    review_request_emails: { ...prev.review_request_emails, enabled: e.target.checked },
                  }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="review_requests_enabled" className="text-sm font-medium text-gray-700">
                  Request reviews after purchase
                </label>
              </div>

              {config.review_request_emails.enabled && (
                <>
                  <Input
                    label="Delay (days)"
                    type="number"
                    value={String(config.review_request_emails.delay_days)}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      review_request_emails: { ...prev.review_request_emails, delay_days: Math.max(1, parseInt(e.target.value) || 14) },
                    }))}
                    min="1"
                    helperText="Days after purchase to send review request"
                  />
                  <Input
                    label="Email Subject"
                    value={config.review_request_emails.email_subject}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      review_request_emails: { ...prev.review_request_emails, email_subject: e.target.value },
                    }))}
                  />
                </>
              )}
            </div>
          </Card>

          <Card title="Back in Stock Alerts" padding="lg">
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="back_in_stock_enabled"
                  checked={config.back_in_stock.enabled}
                  onChange={(e) => setConfig(prev => ({
                    ...prev,
                    back_in_stock: { ...prev.back_in_stock, enabled: e.target.checked },
                  }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="back_in_stock_enabled" className="text-sm font-medium text-gray-700">
                  Send back-in-stock notifications
                </label>
              </div>

              {config.back_in_stock.enabled && (
                <>
                  <Input
                    label="Button Text"
                    value={config.back_in_stock.button_text}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      back_in_stock: { ...prev.back_in_stock, button_text: e.target.value },
                    }))}
                    helperText="Text shown on the notify button on product pages"
                  />
                  <Input
                    label="Email Subject"
                    value={config.back_in_stock.email_subject}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      back_in_stock: { ...prev.back_in_stock, email_subject: e.target.value },
                    }))}
                    helperText="Use {{product_name}} for dynamic product name"
                  />
                </>
              )}
            </div>
          </Card>
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            isLoading={isSaving}
          >
            <Save className="w-5 h-5 mr-2" aria-hidden="true" />
            Save Settings
          </Button>
        </div>
      </form>
    </div>
  );
}
